/**
 * CertificatesTab — Iter387
 * Liste paginée des certificats globaux de formation (admin) + révocation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Award, ShieldOff, ShieldCheck, ChevronLeft, ChevronRight, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';

export default function CertificatesTab({ api, token }) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [loading, setLoading] = useState(true);
  const [onlyRevoked, setOnlyRevoked] = useState(false);
  const [revokeModal, setRevokeModal] = useState(null); // {cert_id, user_name}
  const [revokeReason, setRevokeReason] = useState('');
  const [revoking, setRevoking] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${api}/admin/global-certificates`, {
        params: { page, limit, only_revoked: onlyRevoked },
        headers,
      });
      setItems(r.data?.items || []);
      setTotal(r.data?.total || 0);
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger les certificats');
    } finally {
      setLoading(false);
    }
  }, [api, page, limit, onlyRevoked, token]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const submitRevoke = async () => {
    if (!revokeModal) return;
    if (!revokeReason.trim() || revokeReason.trim().length < 3) {
      toast.error('Précise le motif (3 caractères min.)');
      return;
    }
    setRevoking(true);
    try {
      await axios.post(
        `${api}/admin/global-certificates/${revokeModal.cert_id}/revoke`,
        { reason: revokeReason.trim() },
        { headers },
      );
      toast.success('Certificat révoqué');
      setRevokeModal(null);
      setRevokeReason('');
      fetchList();
    } catch (e) {
      console.error(e);
      toast.error("Échec de la révocation : " + (e.response?.data?.detail || e.message));
    } finally {
      setRevoking(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="space-y-4" data-testid="certificates-tab">
      {/* Filtres + count */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="text-sm text-[#6B5E48]" data-testid="certificates-total">
          {total} certificat{total > 1 ? 's' : ''}
        </div>
        <div className="flex-1" />
        <button
          type="button"
          onClick={() => { setOnlyRevoked(false); setPage(1); }}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            !onlyRevoked
              ? 'bg-[#1F1A12] text-[#F3E9C8] border-[#1F1A12]'
              : 'bg-white text-[#6B5E48] border-[#E8E2D2] hover:border-[#1F1A12]'
          }`}
          data-testid="filter-active"
        >
          Tous
        </button>
        <button
          type="button"
          onClick={() => { setOnlyRevoked(true); setPage(1); }}
          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
            onlyRevoked
              ? 'bg-rose-700 text-white border-rose-700'
              : 'bg-white text-[#6B5E48] border-[#E8E2D2] hover:border-rose-700'
          }`}
          data-testid="filter-revoked"
        >
          Révoqués
        </button>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-2xl border border-[#E8E2D2] overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16" data-testid="certificates-loading">
            <Loader2 className="w-6 h-6 text-[#C49B2A] animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16 text-[#6B5E48]" data-testid="certificates-empty">
            Aucun certificat {onlyRevoked ? 'révoqué ' : ''}à ce jour.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-[#FBF9F3] border-b border-[#E8E2D2]">
              <tr className="text-left text-[10px] uppercase tracking-wider text-[#6B5E48]">
                <th className="px-4 py-3 font-medium">Bénéficiaire</th>
                <th className="px-4 py-3 font-medium">N° officiel</th>
                <th className="px-4 py-3 font-medium">Émis le</th>
                <th className="px-4 py-3 font-medium">Empreinte</th>
                <th className="px-4 py-3 font-medium">Statut</th>
                <th className="px-4 py-3 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((c) => (
                <tr
                  key={c.cert_id}
                  className="border-b border-[#F0EBDC] hover:bg-[#FCFBF6] transition-colors"
                  data-testid={`certificate-row-${c.cert_id}`}
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#1F1A12]">{c.user_name || '—'}</div>
                    <div className="text-[11px] text-[#6B5E48]">{c.user_email || c.user_id}</div>
                  </td>
                  <td className="px-4 py-3">
                    {c.display_number ? (
                      <span className="font-mono text-[11px] font-semibold text-[#92710A]" data-testid={`display-number-${c.cert_id}`}>{c.display_number}</span>
                    ) : (
                      <span className="font-mono text-[10px] text-[#A89F8A] italic">{c.cert_id}</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#1F1A12]">
                    {c.issued_at ? new Date(c.issued_at).toLocaleString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}
                  </td>
                  <td className="px-4 py-3 font-mono text-[10px] text-[#6B5E48]">{(c.signature_v1 || '').slice(0, 12)}…</td>
                  <td className="px-4 py-3">
                    {c.revoked ? (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-rose-100 text-rose-700 border border-rose-200">
                        <ShieldOff className="w-3 h-3" /> Révoqué
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] bg-emerald-100 text-emerald-700 border border-emerald-200">
                        <ShieldCheck className="w-3 h-3" /> Actif
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!c.revoked && (
                      <button
                        type="button"
                        onClick={() => setRevokeModal({ cert_id: c.cert_id, user_name: c.user_name })}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium bg-rose-50 text-rose-700 border border-rose-200 hover:bg-rose-100 transition-colors"
                        data-testid={`revoke-btn-${c.cert_id}`}
                      >
                        Révoquer
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-[#6B5E48]">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="p-1.5 rounded-md border border-[#E8E2D2] hover:bg-[#FBF9F3] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="page-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span data-testid="page-info">Page {page} / {totalPages}</span>
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="p-1.5 rounded-md border border-[#E8E2D2] hover:bg-[#FBF9F3] disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="page-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Revoke modal */}
      {revokeModal && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          data-testid="revoke-modal"
          onClick={() => !revoking && setRevokeModal(null)}
        >
          <div
            className="bg-white rounded-2xl p-6 max-w-md w-full shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-rose-100">
                  <ShieldOff className="w-5 h-5 text-rose-700" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#1F1A12]" style={{ fontFamily: 'Georgia, serif' }}>Révoquer le certificat</h3>
                  <p className="text-xs text-[#6B5E48]">{revokeModal.user_name}</p>
                </div>
              </div>
              <button type="button" onClick={() => !revoking && setRevokeModal(null)} className="text-[#6B5E48] hover:text-[#1F1A12]" data-testid="revoke-close">
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-[#6B5E48] mb-3">
              La révocation est définitive. Le QR code affichera "Certificat révoqué" lors de toute vérification.
            </p>

            <label className="block text-xs uppercase tracking-wider text-[#6B5E48] mb-1.5">Motif (obligatoire)</label>
            <textarea
              value={revokeReason}
              onChange={(e) => setRevokeReason(e.target.value)}
              rows={3}
              maxLength={500}
              className="w-full px-3 py-2 border border-[#E8E2D2] rounded-lg text-sm focus:border-rose-500 focus:outline-none"
              placeholder="Ex. Doublon, plagiat, demande employeur…"
              data-testid="revoke-reason-input"
              disabled={revoking}
            />

            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => setRevokeModal(null)}
                disabled={revoking}
                className="px-4 py-2 rounded-lg text-sm text-[#6B5E48] hover:bg-[#FBF9F3] disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={submitRevoke}
                disabled={revoking}
                className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-rose-700 hover:bg-rose-800 disabled:opacity-50 flex items-center gap-2"
                data-testid="revoke-confirm-btn"
              >
                {revoking ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                Confirmer la révocation
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
